import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Truck, Package, Wine } from "lucide-react";
import { api, AppConfig, errorMessage, Order } from "../lib/api";
import { dateTime, money, shortDate } from "../lib/format";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { History, ShopOrderCard } from "../components/ShopOrderCard";

const steps = [
  { key: "processing", label: "Preparing", icon: Package },
  { key: "shipped", label: "On its way", icon: Truck },
  { key: "delivered", label: "Delivered", icon: Check },
];

export default function OrdersPage() {
  const qc = useQueryClient();
  const { data: history } = useQuery<History>({ queryKey: ["orders-history"], queryFn: () => api.get("/orders/history").then((r) => r.data), staleTime: 60000 });
  const orders = history ? history.orders.filter((r) => r.kind === "app").map((r) => r.app as Order) : undefined;
  const { data: config } = useQuery<AppConfig>({ queryKey: ["config"], queryFn: () => api.get("/config").then((r) => r.data) });

  const act = async (o: Order, action: "cancel" | "advance") => {
    try {
      await api.post(action === "cancel" ? `/orders/${o.id}/cancel` : `/dev/orders/${o.id}/advance`);
      toast.success(action === "cancel" ? "Order cancelled and refunded to your cellar" : "Delivery status updated");
      ["orders", "orders-history", "wallet", "ledger"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <div data-testid="orders-page">
      <div className="eyebrow">Deliveries</div>
      <h1 className="mt-3 font-serif text-4xl font-medium tracking-tight sm:text-5xl">Your orders</h1>
      {history && <p className="mt-3 max-w-2xl text-sm text-neutral-600" data-testid="orders-intro">Everything in one place — cases paid from your cellar{history.counts.web ? `, ${history.counts.web} website order${history.counts.web === 1 ? "" : "s"}` : ""}{history.counts.pos ? ` and ${history.counts.pos} bought in the shop` : ""}.{history.error && <span className="text-[var(--wine)]"> {history.error}.</span>}</p>}
      {history && history.orders.length === 0 && (
        <div className="mt-12 border border-dashed border-border p-12 text-center" data-testid="orders-empty">
          <Wine className="mx-auto h-8 w-8 text-neutral-400" strokeWidth={1} />
          <p className="mt-4 text-sm text-neutral-600">No orders yet. Your first case is waiting.</p>
          <Button asChild variant="outline" className="mt-6 rounded-none"><Link to="/shop">Browse wines</Link></Button>
        </div>
      )}
      <div className="mt-10 space-y-8">
        {(history?.orders || []).map((r) => {
          if (r.kind !== "app") return <ShopOrderCard key={r.shop!.id} o={r.shop!} />;
          const o = r.app as Order;
          const stepIdx = steps.findIndex((s) => s.key === o.status);
          return (
            <article key={o.id} className="border border-border rise" data-testid={`order-card-${o.id}`}>
              <header className="flex flex-wrap items-center justify-between gap-4 border-b border-border bg-[var(--parchment)] px-6 py-4">
                <div className="flex items-center gap-4">
                  <span className="font-serif text-xl">{o.shopify_order_name || `#${o.id.slice(-6).toUpperCase()}`}</span>
                  <span className="text-sm text-neutral-500">{shortDate(o.created_at)}</span>
                  {o.shopify_mock && <span className="border border-dashed border-border px-2 py-0.5 text-[10px] uppercase tracking-widest text-neutral-500">Shopify mock</span>}
                  {o.delivery_method === "collection" && <span className="border border-border px-2 py-0.5 text-[10px] uppercase tracking-widest text-neutral-600" data-testid={`order-collection-${o.id}`}>Collection · Melbourne</span>}
                </div>
                <div className="flex items-center gap-4">
                  <StatusBadge status={o.status} testId={`order-status-${o.id}`} />
                  <span className="font-serif text-xl tabular-nums">{money(o.total_pence)}</span>
                </div>
              </header>
              <div className="grid gap-8 p-6 lg:grid-cols-[1fr_320px]">
                <ul className="space-y-3">
                  {o.items.map((it) => (
                    <li key={it.variant_id} className="flex items-center gap-4 text-sm">
                      <div className="h-14 w-10 shrink-0 overflow-hidden bg-[var(--parchment)]">{it.image && <img src={it.image} alt="" className="h-full w-full object-cover" />}</div>
                      <div className="flex-1"><Link to={`/shop/${it.product_handle}`} className="hover:text-[var(--wine)]">{it.title}</Link>{it.variant_title !== "Default Title" && <span className="text-neutral-500"> · {it.variant_title}</span>}</div>
                      <span className="text-neutral-500">× {it.quantity}</span>
                      <span className="w-20 text-right tabular-nums">{money(it.line_total_pence)}</span>
                    </li>
                  ))}
                </ul>
                <div>
                  {o.status === "cancelled" || o.status === "failed" ? (
                    <p className="text-sm text-neutral-600">{o.status === "cancelled" ? "Cancelled — refunded to your cellar." : "Could not be placed — your cellar was restored."}</p>
                  ) : o.status === "draft" ? (
                    <p className="text-sm text-neutral-600" data-testid={`order-draft-note-${o.id}`}>We've put this case together for you — we're checking the bottles in the cellar now. You'll get your confirmation as soon as it's on its way to being packed.</p>
                  ) : (
                    <ol className="relative space-y-4 border-l border-border pl-6">
                      {steps.map((s, i) => {
                        const done = i <= stepIdx;
                        const hist = o.status_history.find((h) => h.status === s.key);
                        return (
                          <li key={s.key} className="relative text-sm">
                            <span className={`absolute -left-[31px] flex h-5 w-5 items-center justify-center border ${done ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--paper)]" : "border-border bg-[var(--paper)]"}`}><s.icon className="h-3 w-3" strokeWidth={1.5} /></span>
                            <div className={done ? "" : "text-neutral-400"}>{o.delivery_method === "collection" ? ({ processing: "Preparing", shipped: "Ready to collect", delivered: "Collected" } as Record<string, string>)[s.key] || s.label : s.label}</div>
                            {hist && <div className="text-xs text-neutral-500">{dateTime(hist.at)}</div>}
                            {s.key === "shipped" && o.tracking?.number && done && <div className="text-xs text-neutral-500">{o.tracking.company} · {o.tracking.number}</div>}
                          </li>
                        );
                      })}
                    </ol>
                  )}
                  <div className="mt-6 flex flex-wrap gap-2">
                    {o.status === "processing" && <Button variant="outline" size="sm" className="rounded-none" onClick={() => act(o, "cancel")} data-testid={`order-cancel-${o.id}`}>Cancel &amp; refund to cellar</Button>}
                    {config?.shopify_admin_mock && (o.status === "processing" || o.status === "shipped") && (
                      <Button variant="ghost" size="sm" className="rounded-none text-xs text-neutral-500" onClick={() => act(o, "advance")} data-testid={`order-advance-${o.id}`}>Simulate fulfilment update</Button>
                    )}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
